import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreId, getStoreIdFromAuth } from '../utils/auth';
import * as logger from '../utils/logger';
import { showToast } from '../utils/toast';
import Loading from './Loading';
import ErrorMessage from './ErrorMessage';

const Upgrade = () => {
  const { t } = useTranslation();
  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();
  
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [singlePlan, setSinglePlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [ecwidChargeLoading, setEcwidChargeLoading] = useState(false);
  const [ecwidChargeError, setEcwidChargeError] = useState(null);
  const [noPaymentOption, setNoPaymentOption] = useState(null);
  const [continueLoading, setContinueLoading] = useState(false);

  const refreshSubscription = async (options = {}) => {
    const { skipToast } = options;
    if (!hashSiteId) return;
    
    setRefreshing(true);
    try {
      const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
      if (subResponse.ok) {
        const subData = await subResponse.json();
        if (subData.subscription) {
          setCurrentSubscription(subData.subscription);
          if (!skipToast) showToast(t('upgrade.subscriptionRefreshed'), 'success');
        } else {
          setCurrentSubscription(null);
          if (!skipToast) showToast(t('upgrade.noActiveSubscription'), 'info');
        }
      } else {
        if (!skipToast) showToast(t('upgrade.refreshFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error refreshing subscription:', error);
      if (!skipToast) showToast(t('upgrade.refreshFailed'), 'error');
    }
    setRefreshing(false);
  };

  // Fetch pricing and subscription in parallel so the page loads as fast as the slower of the two
  useEffect(() => {
    if (!hashSiteId) {
      setLoading(false);
      return;
    }

    const fetchPricing = async () => {
      const response = await fetch(`${apiUrl}/api/pricing?id=${hashSiteId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch pricing');
      return data;
    };

    const fetchSubscription = async () => {
      const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
      if (!subResponse.ok) return null;
      const subData = await subResponse.json();
      return subData.subscription ?? null;
    };

    (async () => {
      try {
        const [data, subscription, paymentStatus] = await Promise.all([
          fetchPricing(),
          fetchSubscription(),
          fetch(`${apiUrl}/api/ecwid-billing/payment-status?id=${hashSiteId}`).then(r => r.ok ? r.json() : { hasPaymentOption: false }).catch(() => ({ hasPaymentOption: false }))
        ]);

        if (paymentStatus && paymentStatus.hasPaymentOption === false) {
          setNoPaymentOption(true);
        } else {
          setNoPaymentOption(false);
        }

        if (data.subscriptions) {
          const monthlyPlans = data.subscriptions.filter(s => s.subscription_type === 'MONTHLY');
          setSubscriptions(monthlyPlans);
          if (monthlyPlans.length > 0) {
            setSinglePlan(monthlyPlans[0]);
          }
        }

        if (subscription) setCurrentSubscription(subscription);
        setLoading(false);
      } catch (err) {
        logger.error('Error fetching upgrade data:', err);
        setError(err);
        setLoading(false);
      }
    })();
  }, [hashSiteId]);

  const singlePrice = singlePlan?.discounted_price ?? '0';

  const handleCancelSubscription = async () => {
    if (!hashSiteId) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/subscription/cancel?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: currentSubscription?.subscription_id
        })
      });
      
      if (response.ok) {
        setShowCancelModal(false);
        showToast(t('upgrade.cancelSuccess'), 'success');
        await refreshSubscription({ skipToast: true });
      } else {
        const errorData = await response.json();
        showToast(errorData.error || t('upgrade.cancelFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      showToast(t('upgrade.cancelFailedRetry'), 'error');
    }
  };

  const handleContinueSubscription = async () => {
    if (!hashSiteId || !currentSubscription) return;
    setContinueLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/subscription/continue?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: currentSubscription.subscription_id,
          subscription_code: currentSubscription.subscription_name || null
        })
      });
      if (response.ok) {
        showToast(t('upgrade.continueSuccess'), 'success');
        await refreshSubscription();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.error || t('upgrade.continueFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error continuing subscription:', error);
      showToast(t('upgrade.continueFailed'), 'error');
    } finally {
      setContinueLoading(false);
    }
  };

  const handleChoosePlan = () => {
    const plan = singlePlan || subscriptions[0] || {
      id: 1,
      subscription_type: 'MONTHLY',
      code: 'PRO_MONTHLY',
      sku: 'Pro',
      credits: 1000,
      discounted_price: singlePrice,
      per_cost: '0',
      no_of_account_users: 1,
      no_of_products: 1000
    };
    setSelectedPlan(plan);
    setShowPayment(true);
    setEcwidChargeError(null);
  };

  const handleEcwidCharge = async () => {
    if (!hashSiteId || !selectedPlan) return;
    const amount = singlePlan?.discounted_price ?? selectedPlan.discounted_price ?? singlePrice;
    const description = `BulkEditPro Pro - Monthly`;
    const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    setEcwidChargeLoading(true);
    setEcwidChargeError(null);
    try {
      const response = await fetch(`${apiUrl}/api/ecwid-billing/charge?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount) || 0,
          currency: 'USD',
          description,
          idempotencyKey,
          metadata: { plan_id: selectedPlan.id, subscription_code: selectedPlan.sku }
        })
      });
      const data = await response.json();
      if (response.ok) {
        setEcwidChargeError(null);
        showToast(t('upgrade.subscriptionActivated'), 'success');
        setShowPayment(false);
        setSelectedPlan(null);
        const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
        if (subResponse.ok) {
          const subData = await subResponse.json();
          if (subData.subscription) setCurrentSubscription(subData.subscription);
        }
      } else {
        setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
        showToast(data.error || t('upgrade.paymentFailed'), 'error');
      }
    } catch (err) {
      logger.error('Ecwid charge error:', err);
      setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
      showToast(t('upgrade.paymentFailed'), 'error');
    } finally {
      setEcwidChargeLoading(false);
    }
  };

  // const handlePaypalLoad = () => {
  //   logger.log('PayPal SDK loaded successfully');
  //   if (typeof window !== 'undefined' && window.paypal) {
  //     logger.log('PayPal object available:', !!window.paypal);
  //     setPaypalLoaded(true);
  //     if (showPayment && selectedPlan) {
  //       setTimeout(() => {
  //         initializePaypal();
  //       }, 100);
  //     }
  //   } else {
  //     logger.error('PayPal SDK loaded but paypal object not found');
  //   }
  // };

  // const initializePaypal = () => {
  //   if (typeof window === 'undefined' || !window.paypal || !selectedPlan) {
  //     logger.log('PayPal not ready:', {
  //       hasWindow: typeof window !== 'undefined',
  //       hasPaypal: !!window?.paypal,
  //       hasSelectedPlan: !!selectedPlan
  //     });
  //     return;
  //   }

  //   if (paypalInitialized) {
  //     logger.log('PayPal already initialized, skipping...');
  //     return;
  //   }

  //   logger.log('Initializing PayPal buttons for subscription');

  //   const container = document.getElementById('paypal-button-container-pro');
  //   if (!container) {
  //     logger.error('Container not found: paypal-button-container-pro');
  //     return;
  //   }
  //   container.innerHTML = '';
    
  //   try {
  //     window.paypal.Buttons({
  //       style: {
  //         shape: 'rect',
  //         color: 'gold',
  //         layout: 'vertical',
  //         label: 'subscribe'
  //       },
  //       async createSubscription() {
  //         try {
  //           const response = await fetch(`${apiUrl}/api/paypal/create-subscription?id=${hashSiteId}`, {
  //             method: 'POST',
  //             headers: { 'Content-Type': 'application/json' },
  //             body: JSON.stringify({
  //               planid: selectedPlan.code,
  //               subscription_id: String(selectedPlan.id),
  //               quantity: 1
  //             })
  //           });
  //           const orderData = await response.json();
  //           if (orderData.id) return orderData.id;
  //         } catch (error) {
  //           logger.error(error);
  //         }
  //       },
  //       async onApprove(data) {
  //         try {
  //           const response = await fetch(`${apiUrl}/api/paypal/capture-subscription?id=${hashSiteId}`, {
  //             method: 'POST',
  //             headers: { 'Content-Type': 'application/json' },
  //             body: JSON.stringify({
  //               order_id: data.orderID,
  //               subscription_id: data.subscriptionID,
  //               facilitatorAccessToken: data.facilitatorAccessToken
  //             })
  //           });
  //           const captureData = await response.json();
  //           logger.log('<onApprove> captureData:', captureData);
  //           if (response.status === 200) {
  //             showToast(t('upgrade.subscriptionActivated'), 'success');
  //             setShowPayment(false);
  //             const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
  //             if (subResponse.ok) {
  //               const subData = await subResponse.json();
  //               if (subData.subscription) {
  //                 setCurrentSubscription(subData.subscription);
  //               }
  //             }
  //           } else {
  //             showToast(t('upgrade.paymentFailed'), 'error');
  //           }
  //         } catch (error) {
  //           logger.error(error);
  //         }
  //       }
  //     }).render('#paypal-button-container-pro').then(() => {
  //       logger.log('PayPal subscription button rendered successfully');
  //       setPaypalInitialized(true);
  //     }).catch((err) => {
  //       logger.error('Error rendering PayPal subscription button:', err);
  //     });
  //   } catch (err) {
  //     logger.error('Error initializing PayPal subscription button:', err);
  //   }
  // };

  // // Load PayPal SDK
  // useEffect(() => {
  //   if (!paypalClientId) return;
  //   if (document.getElementById('paypal-sdk-script')) return;
    
  //   const script = document.createElement('script');
  //   script.id = 'paypal-sdk-script';
  //   script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&vault=true&disable-funding=paylater&currency=USD&components=buttons`;
  //   script.async = true;
  //   script.onload = handlePaypalLoad;
  //   script.onerror = (e) => {
  //     logger.error('Error loading PayPal SDK:', e);
  //     setError(new Error(t('upgrade.failedToLoadPayPal')));
  //   };
  //   document.body.appendChild(script);
    
  //   return () => {
  //     const existingScript = document.getElementById('paypal-sdk-script');
  //     if (existingScript) {
  //       document.body.removeChild(existingScript);
  //     }
  //   };
  // }, [paypalClientId]);

  // // Initialize PayPal when SDK is loaded, payment section is shown, and method is PayPal
  // useEffect(() => {
  //   if (paymentMethod === 'paypal' && paypalLoaded && showPayment && selectedPlan) {
  //     const timer = setTimeout(() => {
  //       initializePaypal();
  //     }, 300);
  //     return () => clearTimeout(timer);
  //   }
  // }, [paymentMethod, paypalLoaded, showPayment, selectedPlan]);

  // useEffect(() => {
  //   if (paymentMethod === 'paypal' && showPayment && selectedPlan && typeof window !== 'undefined' && window.paypal && !paypalLoaded) {
  //     setPaypalLoaded(true);
  //     const timer = setTimeout(() => {
  //       initializePaypal();
  //     }, 300);
  //     return () => clearTimeout(timer);
  //   }
  // }, [paymentMethod, showPayment, selectedPlan]);

  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;

  // When subscription is suspended, show pricing with selectable plans (same as trial)
  const showSelectablePricing = !currentSubscription || currentSubscription?.status === 'SUSPENDED';

  const billingUrl = getStoreIdFromAuth() ? `https://my.ecwid.com/store/${getStoreIdFromAuth()}#billing` : null;

  return (
    <div style={{ padding: '1.5rem 0 3rem 0' }}>
      {/* No payment option: show error and link to Ecwid billing */}
      {noPaymentOption === true && (
        <div className="container-fluid px-3 mb-3">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <span className="me-2">⚠</span>
            <div className="flex-grow-1">
              {t('upgrade.noPaymentOption')}{' '}
              {billingUrl && (
                <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link fw-bold">
                  {t('upgrade.ecwidBillingPaymentLink')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {currentSubscription ? (
        currentSubscription.status === 'SUSPENDED' ? (
          <div className="container-fluid px-3 mb-3">
            <div className="card border border-warning shadow-sm bg-warning bg-opacity-10">
              <div className="card-body py-3 px-3">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 24, height: 24 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#fff">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                    </svg>
                  </span>
                  <span className="fw-bold text-dark" style={{ fontSize: '1rem' }}>{t('upgrade.suspendedSubscription')}</span>
                </div>
                <p className="text-dark mb-0 mt-2 mb-1" style={{ fontSize: '0.9rem' }}>
                  {t('upgrade.suspendedMessage', {
                    nextBillingDate: currentSubscription.next_billing_date
                      ? new Date(currentSubscription.next_billing_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                      : t('upgrade.na')
                  })}
                </p>
                <div className="d-flex flex-wrap align-items-center gap-3 mt-2" style={{ fontSize: '0.875rem' }}>
                  <span className="text-muted">
                    <span className="text-muted">{t('upgrade.plan')}</span> <strong className="text-dark">{currentSubscription.subscription_name || t('upgrade.proPlan')}</strong>
                  </span>
                  <span className="text-muted">
                    <span className="text-muted">{t('upgrade.price')}</span> <strong className="text-dark">${currentSubscription.price || '0'}{t('upgrade.perMonth')}</strong>
                  </span>
                  <span className="text-muted text-nowrap" title={currentSubscription.subscription_id}>
                    <span className="text-muted">{t('upgrade.id')}</span> {String(currentSubscription.subscription_id).slice(0, 12)}{String(currentSubscription.subscription_id).length > 12 ? '…' : ''}
                  </span>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleContinueSubscription}
                    disabled={continueLoading}
                  >
                    {continueLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {t('upgrade.processing')}
                      </>
                    ) : (
                      t('upgrade.iWantToContinue')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="container-fluid px-3 mb-3">
            <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '12px', borderLeft: '4px solid #198754' }}>
              <div className="card-body p-0">
                <div className="d-flex flex-column flex-md-row align-items-stretch">
                  <div className="d-flex align-items-center px-4 py-3" style={{ background: 'linear-gradient(135deg, #19875408 0%, #19875414 100%)', minWidth: '200px' }}>
                    <span className="bg-success bg-opacity-10 text-success rounded-3 d-flex align-items-center justify-content-center me-3" style={{ width: 48, height: 48 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20v-2h14v2zm0-3.5L3.725 8.475q-.05 0-.113.013T3.5 8.5q-.625 0-1.062-.438T2 7t.438-1.062T3.5 5.5t1.063.438T5 7q0 .175-.038.325t-.087.275L8 9l3.125-4.275q-.275-.2-.45-.525t-.175-.7q0-.625.438-1.063T12 2t1.063.438T13.5 3.5q0 .375-.175.7t-.45.525L16 9l3.125-1.4q-.05-.125-.088-.275T19 7q0-.625.438-1.063T20.5 5.5t1.063.438T22 7t-.437 1.063T20.5 8.5q-.05 0-.112-.012t-.113-.013L19 16.5z" stroke-width="0.5" stroke="currentColor"/></svg>
                    </span>
                    <div>
                      <div className="text-success text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{t('upgrade.activeSubscription')}</div>
                      <div className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>{currentSubscription.subscription_name || t('upgrade.proPlan')}</div>
                    </div>
                  </div>
                  <div className="flex-grow-1 px-4 py-3 d-flex flex-wrap align-items-center gap-4 gap-md-3">
                    <div className="d-flex align-items-baseline gap-1">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.price')}</span>
                      <span className="fw-bold text-dark" style={{ fontSize: '1.25rem' }}>${currentSubscription.price || '0'}</span>
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('upgrade.perMonth')}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.products')}</span>
                      <span className="fw-semibold text-dark">{currentSubscription.product_count || '0'}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.nextBilling')}</span>
                      <span className="text-dark">
                        {currentSubscription.next_billing_date
                          ? new Date(currentSubscription.next_billing_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : t('upgrade.na')}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3 d-flex align-items-center justify-content-between justify-content-md-end gap-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', background: 'rgba(0,0,0,0.02)' }}>
                    <span className="text-muted text-nowrap" style={{ fontSize: '0.75rem' }} title={currentSubscription.subscription_id}>
                      {t('upgrade.id')} {String(currentSubscription.subscription_id).slice(0, 14)}{String(currentSubscription.subscription_id).length > 14 ? '…' : ''}
                    </span>
                    {currentSubscription.subscription_id !== 'ecwid_billing' && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => setShowCancelModal(true)}
                      >
                        {t('upgrade.cancelSubscription')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      ) : (
        <div></div>
        // <div className="text-center py-1">
        //   <div className="bg-secondary bg-opacity-10 rounded-circle d-inline-flex p-1 mb-1">
        //     <svg width="16" height="16" viewBox="0 0 16 16" fill="#6c757d">
        //       <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
        //       <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
        //     </svg>
        //   </div>
        //   <p className="text-muted mb-0 small" style={{ fontSize: '0.85rem' }}>No Active Plan</p>
        // </div>
      )}      

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('upgrade.cancelSubscription')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>{t('upgrade.cancelConfirmMessage')}</p>
                <p className="text-muted small">{t('upgrade.cancelConfirmHint')}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>
                  {t('upgrade.keepSubscription')}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleCancelSubscription}>
                  {t('upgrade.yesCancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* {!paypalClientId && !loading && (
        <div className="alert alert-warning mb-3" style={{ fontSize: '0.875rem' }}>
          <strong>Warning:</strong> PayPal client ID not configured. Please set PAYPAL_CLIENT_ID environment variable.
        </div>
      )} */}

      <div className="container-fluid px-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="fw-bold mb-0" style={{ fontSize: '1.5rem' }}>{t('upgrade.pricingPlans')}</h2>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={refreshSubscription}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('upgrade.refreshing')}
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                  <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                </svg>
                {t('upgrade.refresh')}
              </>
            )}
          </button>
        </div>
        <p className="text-left text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          {t('upgrade.choosePlanIntro')}
        </p>

        {/* Product Count Card */}


        {/* Plans Grid */}
        <div className="row g-4 mb-4">
          {/* Free Plan */}
          {showSelectablePricing && (
            <div className="col-12 col-lg-4">
              <div
                className="card h-100 position-relative"
                style={{
                  borderRadius: '1rem',
                  borderWidth: '1px',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div className="card-body p-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                    style={{
                      width: '48px',
                      height: '48px',
                      background: '#6c757d20',
                      color: '#6c757d'
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                  </div>

                  <h4 className="mb-2">{t('upgrade.free')}</h4>
                  <div className="mb-3">
                    <span className="display-6 fw-bold">$0</span>
                    <span className="text-muted">{t('upgrade.perMonth')}</span>
                  </div>
                  <p className="text-muted small mb-4">{t('upgrade.perfectForGettingStarted')}</p>

                  <div className="mb-3">
                    <button type="button" className="btn btn-secondary w-100" disabled>
                      {t('upgrade.currentPlan')}
                    </button>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                      <span>{t('upgrade.freeBulkProductEditor')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pro Plan */}
          <div className={`col-12 ${showSelectablePricing ? 'col-lg-4' : 'col-lg-6 mx-auto'}`}>
            <div
              className={`card h-100 position-relative ${showSelectablePricing ? 'border-primary' : ''}`}
              style={{
                borderRadius: '1rem',
                borderWidth: showSelectablePricing ? '2px' : '1px',
                transition: 'all 0.2s ease',
                boxShadow: showSelectablePricing ? '0 4px 12px rgba(12, 44, 85, 0.2)' : '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <div className="card-body p-4">
                <div 
                  className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                  style={{
                    width: '48px',
                    height: '48px',
                    background: 'var(--app-primary-light)',
                    color: 'var(--app-primary)'
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
                  </svg>
                </div>

                <h4 className="mb-2">{t('upgrade.pro')}</h4>
                <div className="mb-3">
                  <span className="display-6 fw-bold">${singlePrice}</span>
                  <span className="text-muted">{t('upgrade.perMonth')}</span>
                </div>
                <p className="text-muted small mb-4">{t('upgrade.forGrowingStores')}</p>

                <div className="mb-3">
                  <button
                    type="button"
                    className={`btn ${currentSubscription && currentSubscription.status !== 'SUSPENDED' ? 'btn-success' : 'btn-primary'} w-100`}
                    onClick={handleChoosePlan}
                    disabled={!(showSelectablePricing)}
                    style={{ padding: '0.75rem' }}
                  >
                    {currentSubscription && currentSubscription.status !== 'SUSPENDED' ? t('upgrade.activePlan') : t('upgrade.chooseThisPlan')}
                  </button>
                </div>

                <div className="d-flex flex-column gap-2">
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                    <span>{t('upgrade.unlimitedBulkProductEditor')}</span>
                  </div>                  
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                    <span>{t('upgrade.priorityEmailSupport')}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                    </svg>
                    <span>{t('upgrade.liveChatSupport')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Section */}
          {showPayment && selectedPlan && (
            <div className="col-12 col-lg-4">
              <div 
                className="card h-100"
                style={{ 
                  borderRadius: '1rem',
                  borderWidth: '1px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div className="card-body p-4">
                  <h4 className="mb-3">{t('upgrade.completePurchase')}</h4>
                  <p className="text-muted small mb-3">
                    {t('upgrade.proPlan')} – ${singlePrice}{t('upgrade.perMonth')}
                  </p>

                    <div>
                      {noPaymentOption === true && (
                        <div className="alert alert-danger small mb-3" style={{ fontSize: '0.8rem' }}>
                          {t('upgrade.noPaymentOption')}{' '}
                          {billingUrl && (
                            <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                              {t('upgrade.ecwidBillingPaymentLink')}
                            </a>
                          )}
                        </div>
                      )}
                      {ecwidChargeError && (
                        <div className="alert alert-danger small mb-3" style={{ fontSize: '0.8rem' }}>
                          {ecwidChargeError}{' '}
                          {billingUrl && (
                            <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                              {t('upgrade.ecwidBillingPaymentLink')}
                            </a>
                          )}
                        </div>
                      )}
                      <p className="text-muted small mb-2">{t('upgrade.ecwidBillingHint')}</p>
                      {/* {billingUrl && (
                        <a
                          href={billingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-danger w-100 mb-2"
                          style={{ padding: '0.75rem' }}
                        >
                          {t('upgrade.ecwidBillingPaymentLink')}
                        </a>
                      )} */}
                      <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleEcwidCharge}
                        disabled={ecwidChargeLoading || noPaymentOption === true}
                        style={{ padding: '0.75rem' }}
                      >
                        {ecwidChargeLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            {t('upgrade.processing')}
                          </>
                        ) : (
                          t('upgrade.payWithEcwidButton')
                        )}
                      </button>
                    </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Upgrade;
